/**
 * Theme Utility
 * 
 * Manages CSS custom properties (variables) for theming.
 * Variables are set on :root and cascade into all elements including shadow DOM.
 * 
 * @example
 * // Initialize base theme at app startup
 * initTheme(baseTheme);
 * 
 * // Update specific variables globally
 * updateVariables({
 *   'sb-color-active-500': '#ff0000',
 *   'sb-spacing-m': '20px'
 * });
 * 
 * // Update variables scoped to an element
 * updateVariables({ 'sb-color-neutral-100': '#f0f0f0' }, dialogElement);
 * 
 * // Read a variable value
 * const color = getVariable('sb-color-active-500');
 */

// Base theme with condensed Skybolt design system
export const baseTheme = {
  // Colors - White
  'sb-color-white': '#ffffff',
  
  // Colors - Neutral (Gray)
  'sb-color-neutral-100': '#f5f5f5',
  'sb-color-neutral-200': '#e2e6e9',
  'sb-color-neutral-300': '#bdc2c6',
  'sb-color-neutral-400': '#8e9397',
  'sb-color-neutral-500': '#798084',
  'sb-color-neutral-600': '#656d72',
  'sb-color-neutral-700': '#50595f',
  'sb-color-neutral-800': '#3c464c',
  'sb-color-neutral-900': '#273239',
  
  // Colors - Active (Red)
  'sb-color-active-100': '#fef5f5',
  'sb-color-active-200': '#fdd5d4',
  'sb-color-active-300': '#f99392',
  'sb-color-active-400': '#e85c5b',
  'sb-color-active-500': '#d22e2d',
  'sb-color-active-600': '#a82423',
  'sb-color-active-700': '#6e1817',
  
  // Colors - Success (Green)
  'sb-color-success-100': '#f0fff9',
  'sb-color-success-200': '#c0f3dd',
  'sb-color-success-300': '#4bce97',
  'sb-color-success-400': '#2abb7f',
  'sb-color-success-500': '#22a06b',
  'sb-color-success-600': '#216e4e',
  'sb-color-success-700': '#164b35',
  
  // Colors - Danger (Orange)
  'sb-color-danger-100': '#fff8f1',
  'sb-color-danger-200': '#ffe4cc',
  'sb-color-danger-300': '#ffb87a',
  'sb-color-danger-400': '#ff9940',
  'sb-color-danger-500': '#e67e22',
  'sb-color-danger-600': '#d35400',
  'sb-color-danger-700': '#7d3c00',
  
  // Colors - Warning (Yellow)
  'sb-color-warning-100': '#fffdee',
  'sb-color-warning-200': '#f5e989',
  'sb-color-warning-300': '#eed12b',
  'sb-color-warning-400': '#ddb30e',
  'sb-color-warning-500': '#cf9f02',
  'sb-color-warning-600': '#7f5f01',
  'sb-color-warning-700': '#533f04',
  
  // Spacing
  'sb-spacing-xxs': '2px',
  'sb-spacing-xs': '4px',
  'sb-spacing-s': '8px',
  'sb-spacing-m': '16px',
  'sb-spacing-lg': '24px',
  'sb-spacing-xl': '40px',
  'sb-spacing-xxl': '64px',
  
  // Typography - Display
  'sb-text-display-1-size': '64px',
  'sb-text-display-1-line-height': '88px',
  'sb-text-display-2-size': '48px',
  'sb-text-display-2-line-height': '56px',
  
  // Typography - Headings
  'sb-text-heading-1-size': '32px',
  'sb-text-heading-1-line-height': '40px',
  'sb-text-heading-1-weight': '600',
  'sb-text-heading-2-size': '24px',
  'sb-text-heading-2-line-height': '32px',
  'sb-text-heading-2-weight': '400',
  'sb-text-heading-3-size': '18px',
  'sb-text-heading-3-line-height': '26px',
  'sb-text-heading-3-weight': '600',
  'sb-text-heading-4-size': '16px',
  'sb-text-heading-4-line-height': '26px',
  'sb-text-heading-4-weight': '600',
  
  // Typography - Body
  'sb-text-body-1-size': '16px',
  'sb-text-body-1-line-height': '24px',
  'sb-text-body-2-size': '14px',
  'sb-text-body-2-line-height': '20px',
  
  // Typography - Labels
  'sb-text-label-12-size': '12px',
  'sb-text-label-12-line-height': '12px',
  'sb-text-label-10-size': '10px',
  'sb-text-label-10-line-height': '12px',
  
  // Shadows/Depth
  'sb-shadow-level-0': 'none',
  'sb-shadow-level-1': '0 1px 3px rgba(0, 0, 0, 0.1)',
  'sb-shadow-level-2': '0 4px 8px rgba(0, 0, 0, 0.12), 0 2px 4px rgba(0, 0, 0, 0.08)',
  'sb-shadow-level-3': '0 8px 16px rgba(0, 0, 0, 0.15), 0 4px 8px rgba(0, 0, 0, 0.1)'
};

// Store reference to base theme
let _baseTheme = { ...baseTheme };

/**
 * Normalize a CSS variable name by ensuring it has the -- prefix
 * @param {string} name - Variable name with or without --
 * @returns {string} Normalized name with -- prefix
 */
function normalizeVariableName(name) {
  return name.startsWith('--') ? name : `--${name}`;
}

/**
 * Normalize a value by adding 'px' suffix to bare numbers
 * @param {string|number} value - The value to normalize
 * @returns {string} Normalized value
 */
function normalizeValue(value) {
  if (value == null) return null;
  
  // Convert to string
  const str = String(value);
  
  // If it's a pure number, add 'px'
  if (/^\d+$/.test(str)) {
    return `${str}px`;
  }
  
  return str;
}

/**
 * Set CSS custom properties on a target element
 * @param {Object} variables - Object mapping variable names to values
 * @param {HTMLElement} target - Target element (defaults to document.documentElement)
 */
function setVariables(variables, target = document.documentElement) {
  if (!variables || typeof variables !== 'object') {
    console.warn('[theme] Invalid variables object provided');
    return;
  }
  
  Object.entries(variables).forEach(([name, value]) => {
    const normalizedValue = normalizeValue(value);
    
    if (normalizedValue == null) {
      console.warn(`[theme] Skipping invalid value for "${name}": ${value}`);
      return;
    }
    
    const varName = normalizeVariableName(name);
    target.style.setProperty(varName, normalizedValue);
  });
}

/**
 * Initialize the base theme by setting all variables on :root
 * Should be called once at app initialization
 * 
 * @param {Object} theme - Theme object mapping variable names to values
 * @example
 * initTheme({
 *   'sb-color-white': '#ffffff',
 *   'sb-spacing-m': '16px'
 * });
 */
export function initTheme(theme = baseTheme) {
  _baseTheme = { ...theme };
  setVariables(theme);
}

/**
 * Update specific CSS variables globally or on a target element
 * Merges with existing variables rather than replacing them
 * 
 * @param {Object} overrides - Object mapping variable names to new values
 * @param {HTMLElement} [target] - Optional target element (defaults to :root)
 * @example
 * // Update globally
 * updateVariables({
 *   'sb-color-active-500': '#ff0000',
 *   'sb-spacing-m': 20  // Will be converted to '20px'
 * });
 * 
 * // Update on specific element
 * updateVariables({ 'sb-color-neutral-100': '#f0f0f0' }, dialogElement);
 */
export function updateVariables(overrides, target) {
  setVariables(overrides, target);
}

/**
 * Get the computed value of a CSS variable
 * 
 * @param {string} name - Variable name (with or without -- prefix)
 * @param {HTMLElement} [element] - Element to read from (defaults to :root)
 * @returns {string} The computed variable value, or empty string if not found
 * @example
 * const color = getVariable('sb-color-active-500');
 * const spacing = getVariable('--sb-spacing-m', element);
 */
export function getVariable(name, element = document.documentElement) {
  const varName = normalizeVariableName(name);
  const value = getComputedStyle(element).getPropertyValue(varName).trim();
  
  if (!value && !_baseTheme[name.replace(/^--/, '')]) {
    console.warn(`[theme] Variable "${name}" does not exist in base theme`);
  }
  
  return value;
}

/**
 * Get all base theme variables
 * @returns {Object} Copy of the base theme object
 */
export function getBaseTheme() {
  return { ..._baseTheme };
}